// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState, xCustomProductDetailsState } from "@hardwoods/App/Store/Reducers";
import { getLineNotes, getLineNotesHasError, getProductAdditionalInfos } from "@hardwoods/App/Store/Selectors/CustomProductDetails";
import ProductAddToCartButton from "@hardwoods/Overrides/Components/ProductAddToCartButton";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    subscription: state.pages.productDetails.subscription,
    lineNotes: getLineNotes(state),
    lineNotesHasError: getLineNotesHasError(state),
    productAdditionalInfos: getProductAdditionalInfos(state),
});

export interface ProductDetailsAddToCartButtonStyles {
    button?: ButtonPresentationProps;
}

export const addToCartButtonStyles: ProductDetailsAddToCartButtonStyles = {
    button: {
        sizeVariant: "medium",
        css: css`
            padding: 0 5px;
            font-weight: bold;
            flex-grow: 1;
            flex-basis: 90px;
            font-size: 12px;
        `,
    },
};

const styles = addToCartButtonStyles;

const ProductDetailsAddToCartButton = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    lineNotes,
    lineNotesHasError,
    productAdditionalInfos,
}: Props) => {
    const [disableAddToCart, setDisableAddToCart] = useState(false);

    useEffect(() => {
        if (productAdditionalInfos.itemSpecIsRequired === "True" && !lineNotes && lineNotesHasError) {
            setDisableAddToCart(true);
        } else {
            setDisableAddToCart(false);
        }
    }, [lineNotes, lineNotesHasError]);

    return (
        <DisablerContext.Provider value={{ disable: disableAddToCart }}>
            <ProductAddToCartButton
                configurationSelection={configurationSelection}
                configurationCompleted={configurationCompleted}
                variantSelectionCompleted={variantSelectionCompleted}
                subscription={subscription}
                extendedStyles={styles.button}
                data-test-selector="addProductToCart"
                isProductDetailsPage={true}
            />
        </DisablerContext.Provider>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductDetailsAddToCartButton),
    definition: {
        displayName: "Add to Cart Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
